import { useState } from "react";
import { Modal, Form, Input, Button, Tooltip } from "antd";
import {
  FolderIcon,
  ArchiveBoxIcon,
  DocumentTextIcon,
  PhotoIcon,
  VideoCameraIcon,
  MusicalNoteIcon,
  CodeBracketIcon,
  CircleStackIcon,
  LockClosedIcon,
  StarIcon,
  HeartIcon,
  UserGroupIcon,
} from "@heroicons/react/24/solid";
import { FOLDER_COLORS, FOLDER_ICONS } from "@/utils/constants";

const iconMap: Record<string, React.ComponentType<React.SVGProps<SVGSVGElement>>> = {
  folder: FolderIcon,
  "folder-open": ArchiveBoxIcon,
  file: DocumentTextIcon,
  picture: PhotoIcon,
  video: VideoCameraIcon,
  sound: MusicalNoteIcon,
  code: CodeBracketIcon,
  database: CircleStackIcon,
  lock: LockClosedIcon,
  star: StarIcon,
  heart: HeartIcon,
  team: UserGroupIcon,
};

interface NewFolderModalProps {
  open: boolean;
  parentFolderId: string | null;
  onClose: () => void;
  onCreate: (name: string, color: string, icon: string) => Promise<void>;
}

export default function NewFolderModal({
  open,
  onClose,
  onCreate,
}: NewFolderModalProps) {
  const [form] = Form.useForm();
  const [selectedColor, setSelectedColor] = useState(FOLDER_COLORS[0]);
  const [selectedIcon, setSelectedIcon] = useState<string>(FOLDER_ICONS[0].key);
  const [isCreating, setIsCreating] = useState(false);

  const handleCreate = async () => {
    try {
      const values = await form.validateFields();
      setIsCreating(true);
      await onCreate(values.name, selectedColor, selectedIcon);
      form.resetFields();
      setSelectedColor(FOLDER_COLORS[0]);
      setSelectedIcon(FOLDER_ICONS[0].key);
      onClose();
    } catch {
      // Validation error
    } finally {
      setIsCreating(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setSelectedColor(FOLDER_COLORS[0]);
    setSelectedIcon(FOLDER_ICONS[0].key);
    onClose();
  };

  const getIconComponent = (iconKey: string, color: string, size = 18) => {
    const IconComponent = iconMap[iconKey] || FolderIcon;
    return <IconComponent style={{ width: size, height: size, color }} />;
  };

  return (
    <Modal
      title="Create new folder"
      open={open}
      onCancel={handleCancel}
      width={480}
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          Cancel
        </Button>,
        <Button
          key="create"
          type="primary"
          loading={isCreating}
          onClick={handleCreate}
        >
          Create
        </Button>,
      ]}
    >
      <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
        <Form.Item
          name="name"
          label="Folder name"
          rules={[
            { required: true, message: "Please enter a folder name" },
            { max: 255, message: "Folder name is too long" },
            {
              pattern: /^[^\\/:*?"<>|]+$/,
              message: "Folder name contains invalid characters",
            },
          ]}
        >
          <Input
            prefix={
              <span style={{ marginRight: 8, display: "flex", alignItems: "center" }}>
                {getIconComponent(selectedIcon, selectedColor)}
              </span>
            }
            placeholder="Enter folder name"
            autoFocus
            size="large"
          />
        </Form.Item>

        <Form.Item label="Color" style={{ marginBottom: 16 }}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {FOLDER_COLORS.map((color) => (
              <div
                key={color}
                onClick={() => setSelectedColor(color)}
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 8,
                  backgroundColor: color,
                  cursor: "pointer",
                  border: selectedColor === color
                    ? "3px solid #1F2937"
                    : "3px solid transparent",
                  transition: "all 0.15s ease",
                  transform: selectedColor === color ? "scale(1.1)" : "scale(1)",
                }}
              />
            ))}
          </div>
        </Form.Item>

        <Form.Item label="Icon" style={{ marginBottom: 0 }}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {FOLDER_ICONS.map((icon) => (
              <Tooltip
                key={icon.key}
                title={icon.label}
                placement="top"
                mouseEnterDelay={0.3}
              >
                <div
                  onClick={() => setSelectedIcon(icon.key)}
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: 12,
                    backgroundColor: selectedIcon === icon.key ? `${selectedColor}15` : "#F5F5F5",
                    border: selectedIcon === icon.key
                      ? `2px solid ${selectedColor}`
                      : "2px solid transparent",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "all 0.2s ease",
                    boxShadow: selectedIcon === icon.key
                      ? `0 2px 8px ${selectedColor}30`
                      : "none",
                  }}
                  onMouseEnter={(e) => {
                    if (selectedIcon !== icon.key) {
                      e.currentTarget.style.backgroundColor = "#EBEBEB";
                      e.currentTarget.style.transform = "scale(1.05)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedIcon !== icon.key) {
                      e.currentTarget.style.backgroundColor = "#F5F5F5";
                      e.currentTarget.style.transform = "scale(1)";
                    }
                  }}
                >
                  {getIconComponent(
                    icon.key,
                    selectedIcon === icon.key ? selectedColor : "#9CA3AF",
                    20
                  )}
                </div>
              </Tooltip>
            ))}
          </div>
        </Form.Item>
      </Form>
    </Modal>
  );
}
